import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { usePermissions } from '@/hooks/usePermissions';
import { useSession } from '@/hooks/useSession';
import { useMenuPortal } from '@/hooks/useMenuPortal';
import { showToast } from '@/lib/toast';
import { deleteClient } from '@/api/clients';

/**
 * Меню действий строки – «Открыть профиль»/«Редактировать» ведут на карточку
 * клиента; «Редактировать» сразу открывает её в режиме редактирования
 * (state.autoEdit, см. ClientDetailPage). «Удалить» – та же логика, что и
 * кнопка удаления на самой карточке клиента (useConfirmDialog + deleteClient),
 * после успеха список перезапрашивается через onDeleted (а не просто убирается
 * строка локально – порядок/пагинация могли бы разъехаться).
 *
 * Позиционирование дропдауна — общий useMenuPortal (тот же, что у TileMenu).
 */
export function ClientRowMenu({
  clientId,
  clientName,
  onDeleted,
  isDeleting,
  onDeletingChange,
}: {
  clientId: number;
  clientName: string | null;
  onDeleted: () => void;
  isDeleting: boolean;
  onDeletingChange: (id: number | null) => void;
}) {
  const { confirm, dialog: confirmDialog } = useConfirmDialog();
  const { can } = usePermissions();
  // Карточка — по .view/.add/.edit, правка — по .add/.edit; удаление здесь и
  // так гейтится маршрутом, но пункты меню без прав показывать незачем.
  const canView = can('platform*.customers.view', 'platform*.customers.add', 'platform*.customers.edit');
  const canEdit = can('platform*.customers.add', 'platform*.customers.edit');
  const { refreshPortfolioSize } = useSession();

  const itemCount = 1 + (canView ? 1 : 0) + (canEdit ? 1 : 0);
  const { open, coords, triggerRef, dropdownRef, toggle, close } = useMenuPortal(itemCount);

  const onDelete = async () => {
    close();
    const ok = await confirm(`Удалить клиента «${clientName ?? 'Без названия'}»? Это действие необратимо.`, {
      confirmLabel: 'Удалить',
      danger: true,
    });
    if (!ok) return;
    onDeletingChange(clientId);
    try {
      await deleteClient(clientId);
      showToast('Клиент удалён');
      onDeleted();
      refreshPortfolioSize();
    } catch {
      showToast('Не удалось удалить клиента.');
    } finally {
      onDeletingChange(null);
    }
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="row-menu__trigger"
        aria-label="Действия"
        onClick={toggle}
        disabled={isDeleting}
      >
        <Icon name="kebab" size={16} />
      </button>
      {open &&
        coords &&
        createPortal(
          <div
            className="row-menu__dropdown"
            style={{ top: coords.top, bottom: coords.bottom, right: coords.right }}
            ref={dropdownRef}
          >
            {canView && (
              <Link className="row-menu__item" to={`/clients/${clientId}`} onClick={close}>
                <Icon name="eye" size={15} /> Открыть профиль
              </Link>
            )}
            {canEdit && (
              <Link
                className="row-menu__item"
                to={`/clients/${clientId}`}
                state={{ autoEdit: true }}
                onClick={close}
              >
                <Icon name="pencil" size={15} /> Редактировать
              </Link>
            )}
            <button type="button" className="row-menu__item row-menu__item--danger" onClick={onDelete}>
              <Icon name="trash" size={15} /> Удалить
            </button>
          </div>,
          document.body,
        )}
      {confirmDialog}
    </>
  );
}
